import { subDays, startOfDay } from 'date-fns'
import { DateRangePicker, RangeKeyDict } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import '../styles/date-range-picker.css'
import { useState, useRef, useEffect } from 'react'

type Preset = 7 | 15 | 30 | 90 | 365

export interface Range {
  start: Date
  end: Date
  preset?: Preset
}

interface Props {
  value: Range
  onChange: (r: Range) => void
}

export function DateRangeSelector({ value, onChange }: Props) {
  const [showCalendar, setShowCalendar] = useState(false)
  const [tempRange, setTempRange] = useState<Range>(value)
  const containerRef = useRef<HTMLDivElement>(null)
  const presets: Preset[] = [7, 15, 30, 90, 365]

  const setPreset = (days: Preset) => {
    const end = startOfDay(new Date())
    const start = subDays(end, days - 1)
    onChange({ start, end, preset: days })
    setShowCalendar(false)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setShowCalendar(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const applyDateRange = () => {
    onChange(tempRange)
    setShowCalendar(false)
  }

  return (
    <div className="relative" ref={containerRef}>
      {/* Preset Buttons */}
      <div className="flex flex-wrap gap-2">
        {presets.map((d) => {
          const active = value.preset === d
          return (
            <button
              key={d}
              onClick={() => setPreset(d)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {d === 365 ? '1 year' : `${d}d`}
            </button>
          )
        })}

        {/* Custom Range Toggle */}
        <button
          onClick={() => setShowCalendar(!showCalendar)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
            !value.preset ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Custom
          <svg
            className={`w-4 h-4 transition-transform ${showCalendar ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Calendar Dropdown */}
      {showCalendar && (
        <div className="absolute z-50 mt-2 right-0 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <DateRangePicker
            ranges={[
              {
                startDate: tempRange.start,
                endDate: tempRange.end,
                key: 'selection'
              }
            ]}
            onChange={(r: RangeKeyDict) =>
              setTempRange({
                start: r.selection!.startDate!,
                end: r.selection!.endDate!
              })
            }
            maxDate={new Date()}
            rangeColors={['#2563eb']}
          />

          <div className="flex justify-end p-3 border-t border-gray-100 bg-gray-50">
            <button
              onClick={() => setShowCalendar(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg mr-2"
            >
              Cancel
            </button>
            <button
              onClick={applyDateRange}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DateRangeSelector
